import { useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type { ExecSessionCloseReason, ExecSessionRecord } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { useExecSessions, useScope, useTerminateExecSession } from "@/api/queries";
import { Panel, StatusBadge } from "@/components/primitives";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Breadcrumb, currentRouteCrumb, routeCrumbAt } from "@/components/drill";
import { PageHeader } from "@/features/drill/common";
import { dayClock, since } from "@/lib/format";
import { ExecTranscriptView } from "./ExecTranscriptView";

/**
 * Debug Sessions 관리 화면 (ADR 0030)
 * --------------------------------------------------------------------------
 * platform.admin이 접근 가능한 클러스터 전체의 Debug Terminal 세션을 보고 강제 종료합니다.
 * 일반 사용자는 nav에 이 화면이 없고, 서버도 자기 세션만 돌려줍니다.
 *
 * **폴링하지 않습니다.** 목록은 마운트·수동 새로고침·강제 종료 성공 후, 그리고 SSE의 세션 변경
 * 신호(`execsession`, ADR 0030 결정 2)로 갱신합니다 — 재조회일 뿐 화면을 다시 마운트하지 않습니다.
 * 열린 세션과 최근 24시간 이력은 탭으로 나눕니다. 탭은 URL(`view`)에 둡니다.
 * **목록에 터미널 본문은 없습니다** — 무엇을 열었는가(대상·시각·전송량·상태)만 보입니다.
 * 기록이 켜진 배포(ADR 0031)에서는 행마다 "기록"으로 서버가 마스킹해 저장한 출력을 아래
 * 패널에서 봅니다. 선택은 URL(`transcript`)에 두어 새로고침이 지우지 않습니다.
 */

/** 종료 사유의 짧은 라벨. 터미널의 사용자 문구(서버 소유)와 달리 목록용 요약입니다. */
const REASON_LABEL: Partial<Record<ExecSessionCloseReason, string>> = {
  completed: "정상 종료",
  idle_timeout: "유휴 종료",
  absolute_timeout: "최대 시간",
  client_closed: "사용자 닫음",
  terminated_by_operator: "강제 종료됨",
  auth_expired: "인증 만료",
  lease_lost: "예약 상실",
  upstream_error: "원격 오류",
  pod_replaced: "Pod 교체",
  server_shutdown: "서버 종료",
  feature_disabled: "기능 꺼짐",
};

function reasonLabel(reason?: ExecSessionCloseReason): string {
  if (!reason) return "종료됨";
  return REASON_LABEL[reason] ?? reason;
}

function podHref(r: ExecSessionRecord): string {
  const q = new URLSearchParams({ ns: r.namespace, uid: r.podUid });
  return `/pods/${encodeURIComponent(r.podName)}?${q.toString()}`;
}

export function DebugSessions() {
  const { search } = useLocation();
  const [params, setParams] = useSearchParams();
  const transcriptId = params.get("transcript");
  const scope = useScope();
  const canManage = scope.data?.canManageExecSessions ?? false;
  const sessions = useExecSessions(canManage);
  const terminate = useTerminateExecSession();
  const [target, setTarget] = useState<ExecSessionRecord | null>(null);
  /* 강제 종료가 거절된 사유. 403(권한)·404(이미 끝남·없음)는 서버 문장을 그대로 보입니다. */
  const [failure, setFailure] = useState("");
  const view = params.get("view") === "history" ? "history" : "live";

  const header = (
    <PageHeader
      title="Debug Sessions"
      subtitle="열려 있는 Debug Terminal 세션과 최근 이력 · 강제 종료"
      crumbs={<Breadcrumb items={[routeCrumbAt("overview", "/", search), currentRouteCrumb("debug-sessions")]} />}
      controls={
        <button
          type="button"
          className="linkish"
          onClick={() => sessions.refetch()}
          disabled={sessions.isFetching}
        >
          {sessions.isFetching ? "새로고침 중…" : "새로고침"}
        </button>
      }
    />
  );

  if (!canManage) {
    return (
      <div className="page">
        {header}
        <section className="panel">
          <div className="panel__body">
            <div className="state" role="alert">
              <span className="state__title">권한이 없습니다</span>
              <span className="muted">Debug Sessions 관리는 platform.admin 전용이며, 세션 레지스트리가 켜진 배포에서만 열립니다.</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const data = sessions.data;
  const all = data?.sessions ?? [];
  const liveCount = all.filter((r) => r.live).length;
  const rows = all.filter((r) => (view === "live" ? r.live : !r.live));
  const now = new Date().toISOString();

  const confirmTerminate = () => {
    if (!target) return;
    setFailure("");
    terminate.mutate(target.sessionId, {
      onError: (e) => {
        setFailure(e instanceof HttpError ? e.body.message : "세션을 강제 종료하지 못했습니다. 잠시 후 다시 시도하세요.");
        // 이미 끝난 세션이면 목록이 낡은 것입니다. 다시 받아 행을 "종료됨"으로 옮깁니다.
        void sessions.refetch();
      },
      onSettled: () => setTarget(null),
    });
  };
  const selectView = (next: "live" | "history") =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "history") p.set("view", "history");
        else p.delete("view");
        return p;
      },
      { replace: true },
    );
  const selectTranscript = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("transcript", id);
        else next.delete("transcript");
        return next;
      },
      { replace: true },
    );

  return (
    <div className="page">
      {header}
      <Panel
        title={
          <>
            세션{" "}
            {data && (
              <span className="muted">
                · {liveCount}개 열림 · {all.length - liveCount}개 이력
                {data.isAdmin ? " · 전체 클러스터" : " · 내 세션"}
                {data.truncated ? " · 이력 100건에서 잘림" : ""}
              </span>
            )}
          </>
        }
      >
        <div className="panel__body">
          <div className="exec-toolbar" role="group" aria-label="세션 보기">
            <button type="button" className="chip" aria-pressed={view === "live"} onClick={() => selectView("live")} data-testid="debug-sessions-tab-live">
              열린 세션
            </button>
            <button type="button" className="chip" aria-pressed={view === "history"} onClick={() => selectView("history")} data-testid="debug-sessions-tab-history">
              최근 24시간
            </button>
          </div>
          {failure ? (
            <div className="notice notice--warning" role="alert" data-testid="debug-sessions-failure">
              {failure}
            </div>
          ) : null}
          {sessions.isLoading ? (
            <div className="state">
              <span className="muted">불러오는 중…</span>
            </div>
          ) : sessions.isError ? (
            <div className="state" role="alert">
              <span className="state__title">세션을 불러오지 못했습니다</span>
              <span className="muted">잠시 후 새로고침하세요.</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="state">
              <span className="state__title">{view === "live" ? "열린 세션이 없습니다" : "최근 24시간 이력이 없습니다"}</span>
              <span className="muted">
                {view === "live" ? "지금 열려 있는 Debug Terminal 세션이 없습니다." : "끝난 세션은 24시간 동안 이 탭에 남습니다(최근 100건)."}
              </span>
            </div>
          ) : (
            <div className="table-scroll" style={{ overflowX: "auto" }}>
              <table className="ds-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>상태</th>
                    <th>사용자</th>
                    <th>클러스터 / Namespace</th>
                    <th>Pod · Container</th>
                    <th>시작</th>
                    <th className="ds-num" style={{ textAlign: "right" }}>전송(In/Out)</th>
                    <th>Replica</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {rows.map((r) => {
                    const terminating = r.live && !!r.terminatedBy;
                    return (
                      <tr key={r.sessionId}>
                        <td>
                          {r.live ? (
                            terminating ? (
                              <StatusBadge severity="warning" label="종료 중" small />
                            ) : (
                              <StatusBadge severity="progressing" label="열림" small />
                            )
                          ) : (
                            <>
                              <StatusBadge severity="healthy" label="종료됨" small />{" "}
                              <span className="muted" title={r.reason ?? ""}>
                                {reasonLabel(r.reason)}
                              </span>
                            </>
                          )}
                        </td>
                        <td className="ds-ident">{r.subject || "—"}</td>
                        <td>
                          {r.clusterId} <span className="muted">/ {r.namespace}</span>
                        </td>
                        <td>
                          <Link to={podHref(r)} className="ds-ident">
                            {r.podName}
                          </Link>{" "}
                          <span className="muted">· {r.container}</span>
                        </td>
                        <td className="muted" title={r.startedAt}>
                          {r.live ? since(r.startedAt, now) : dayClock(Date.parse(r.startedAt))}
                          {r.terminatedBy && !r.live ? <span className="muted"> · {r.terminatedBy}</span> : null}
                        </td>
                        <td className="ds-num" style={{ textAlign: "right" }}>
                          {r.inputBytes}/{r.outputBytes}
                        </td>
                        <td className="muted">{r.replica || "—"}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {data?.transcriptsEnabled ? (
                            <button
                              type="button"
                              className="chip"
                              aria-pressed={transcriptId === r.sessionId}
                              onClick={() => selectTranscript(r.sessionId)}
                            >
                              기록
                            </button>
                          ) : null}{" "}
                          {r.live && !terminating ? (
                            <button type="button" className="btn btn--warning" onClick={() => setTarget(r)}>
                              강제 종료
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      {transcriptId && data?.transcriptsEnabled ? (
        <Panel title="세션 기록">
          <div className="panel__body">
            <ExecTranscriptView sessionId={transcriptId} onClose={() => selectTranscript(null)} />
          </div>
        </Panel>
      ) : null}

      <ConfirmDialog
        open={target !== null}
        title="세션을 강제 종료할까요?"
        message={
          target
            ? `${target.subject || "사용자"}의 ${target.namespace}/${target.podName} 세션을 종료합니다. 처리 인스턴스의 다음 갱신 주기(수 초) 안에 셸이 끊깁니다.`
            : ""
        }
        confirmLabel="강제 종료"
        tone="critical"
        busy={terminate.isPending}
        onConfirm={confirmTerminate}
        onCancel={() => setTarget(null)}
      />
    </div>
  );
}
